import React from 'react';
import { UpgradeStrip } from './UpgradeStrip';
import styled, { css } from 'styled-components/macro';
import cx from 'classnames';
import { PopoverSitePicker } from './PopoverSitePicker';
import { MobileMenuDrawer } from './MobileMenu/MobileMenuDrawer';
import { SearchBoxComponent } from 'src/client/containers/SearchBox/SearchBoxComponent';
import { RecentsBox } from './RecentsBox/RecentsBox';
import { SiteNotificationsPanel } from '../SiteNotifications/SiteNotifications';
import { ConstrainedContainer, TABLET_LANDSCAPE, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { PromoHeaderRibbon } from 'src/client/containers/Promo/PromoHeaderRibbon';
import { PartnerHeaderBanner } from 'src/client/components/PartnerBanners/PartnerHeaderBanner';
import { useWidePageContext } from '../App/WidePageContextProvider';
import { useStaticSplashPage } from 'src/client/helpers/useFeatureFlags';
import { HeaderMenuItems } from './HeaderMenuItems';
import { useHeaderBarController } from './helpers/useHeaderBarController';
import { HeaderStrip } from './HeaderStrip';
import { SiteLogo } from './SiteLogo';
import { MobileMenuToggle } from './MobileMenu/MobileMenuToggle';

export const HeaderBar = () => {
  const {
    closeNotificationsPanel,
    closeRecentsBox,
    closeSearchBar,
    handleMobileMenuScrimClick,
    headerMenuController,
    mainHeaderRef,
    mobileMenuOpened,
    notificationsOpen,
    recentsOpen,
    searchBarOpen,
    searchToggleRef,
    toggleMobileMenu,
  } = useHeaderBarController();

  return (
    <>
      <HeaderBarWrapper ref={mainHeaderRef} className={cx({ 'is-guest': !headerMenuController.hasUser })}>
        <PromoHeaderRibbon />

        <PartnerHeaderBanner />

        <UpgradeStrip />

        <HeaderStrip />

        <HeaderContainerSwitcher>
          <HeaderFlexContainer>
            <PopoverSitePicker />

            <SiteLogo />

            <HeaderMenuItems controller={headerMenuController} />

            <MobileMenuToggle mobileMenuOpened={mobileMenuOpened} toggleMobileMenu={toggleMobileMenu} />
          </HeaderFlexContainer>
        </HeaderContainerSwitcher>

        <MobileMenuDrawer />
      </HeaderBarWrapper>

      <SearchBoxComponent isOpened={searchBarOpen} onClose={closeSearchBar} searchToggleRef={searchToggleRef} />

      <RecentsBox isOpened={recentsOpen} onClose={closeRecentsBox} />

      <SiteNotificationsPanel active={notificationsOpen} onClose={closeNotificationsPanel} />

      <MobileMenuScrim show={mobileMenuOpened} onClick={handleMobileMenuScrimClick} />
    </>
  );
};

// @TODO
// Remove after all pages are converted to wide mode.
const HeaderContainerSwitcher: React.FC<{}> = ({ children }) => {
  const widePage = useWidePageContext();
  const { isStaticSplashPage } = useStaticSplashPage();

  if (isStaticSplashPage) {
    return <StaticSplashHeaderContainer>{children}</StaticSplashHeaderContainer>;
  }

  if (widePage) {
    return (
      <Container as={HeaderContainer} extended>
        {children}
      </Container>
    );
  }

  return <Container className="container">{children}</Container>;
};

const StaticSplashHeaderContainer = styled.div`
  padding: 0 5px;
`;

const HeaderContainer = styled(ConstrainedContainer)`
  padding: 0 5px;
  margin: 0 auto;

  @media ${TABLET_PORTRAIT} {
    padding: 0;
  }
`;

const MobileMenuScrim = styled.div<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: #000;
  opacity: 0.6;
  z-index: 1000;
  display: ${(props) => (props.show ? 'block' : 'none')};

  @media ${TABLET_LANDSCAPE} {
    display: none;
  }
`;

const Container = styled.div<{ extended?: boolean }>`
  padding: 0 5px;
  margin: 0 auto;

  @media ${TABLET_PORTRAIT} {
    padding: 0;
  }

  a,
  i {
    transition: 0.3s color;
  }

  .image-btn,
  a:not(.btn):not(.active),
  .navbar-nav > li > a {
    color: ${(props) => props.theme.headerBarPrimary2};

    &:hover {
      text-decoration: none;
      color: ${(props) => props.theme.headerBarPrimary4};
    }
  }

  ${(props) =>
    props.extended &&
    css`
      overflow: hidden;
    `}
`;

const HeaderBarWrapper = styled.div`
  --header-row-height: 50px;

  background: ${(props) => props.theme.headerBarPrimary1};
  position: sticky;
  top: 0;
  z-index: 1001;
  border-bottom: 1px solid ${(props) => props.theme.footerBarPrimary4};

  @media ${TABLET_PORTRAIT} {
    --header-row-height: 64px;
  }

  .fa {
    vertical-align: middle;
  }
`;

const HeaderFlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: nowrap;
  flex-direction: row;
  width: 100%;
  min-height: var(--header-row-height);
`;
